import React, { useCallback, useEffect, useRef, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import useHighlightParam from "@/hooks/useHighlightParam";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DocPreviewDialog } from "@/components/DocPreviewDialog";
import { DocThumb } from "@/components/DocThumb";
import { toast } from "sonner";
import TaxOverviewCard from "@/components/TaxOverviewCard";
import { FolderOpen, Folder, FolderPlus, Plus, Trash2, Edit3, Loader2, UploadCloud, Eye, Building2, Sparkles, Search, Tag, Calendar } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH") : "—");
const EMPTY = { title: "", folder: "", tags: "", doc_date: "", summary: "" };

export default function Documents() {
  const { company } = useCompany();
  const companyId = company?._id || company?.id;
  const [scope, setScope] = useState("company");
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useHighlightParam(items, (d) => d.id);

  const loadFolders = useCallback(async () => {
    if (!companyId) return;
    try { const r = await apiClient.get(`/companies/${companyId}/documents/folders`); setFolders(r.data?.folders || []); } catch { /* ignore */ }
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (folder) params.set("folder", folder);
      const url = (scope === "all" ? "/documents" : `/companies/${companyId}/documents`) + (params.toString() ? `?${params}` : "");
      const r = await apiClient.get(url);
      setItems(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Dokumente konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, [companyId, scope, folder, q]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const addFolder = async () => {
    const name = window.prompt("Name des neuen Ordners:");
    if (!name?.trim()) return;
    try { await apiClient.post(`/companies/${companyId}/documents/folders`, { name: name.trim() }); toast.success(`Ordner «${name.trim()}» angelegt`); loadFolders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Ordner konnte nicht angelegt werden."); }
  };

  const upload = async () => {
    if (!uploadFile) { toast.error("Bitte eine Datei wählen."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (form.folder) fd.append("folder", form.folder);
      if (form.title.trim()) fd.append("title", form.title.trim());
      if (form.tags.trim()) fd.append("tags", form.tags);
      if (form.doc_date) fd.append("doc_date", form.doc_date);
      const r = await apiClient.post(`/companies/${companyId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`«${r.data?.title}» im Ordner «${r.data?.folder}» abgelegt`);
      setUploadOpen(false); setUploadFile(null); setForm(EMPTY);
      load(); loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen."); }
    finally { setUploading(false); }
  };

  const openEdit = (d) => { setEditDoc(d); setForm({ title: d.title || "", folder: d.folder || "", tags: (d.tags || []).join(", "), doc_date: d.doc_date || "", summary: d.summary || "" }); };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/companies/${editDoc.company_id}/documents/${editDoc.id}`, {
        title: form.title.trim() || undefined, folder: form.folder || undefined, doc_date: form.doc_date || undefined, summary: form.summary || undefined,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Dokument aktualisiert."); setEditDoc(null); load(); loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`«${d.title}» löschen?`)) return;
    try { await apiClient.delete(`/companies/${d.company_id}/documents/${d.id}`); toast.success("Gelöscht."); load(); loadFolders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
  };

  const folderOptions = folders.map((f) => f.name);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <TaxOverviewCard companyId={companyId} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><FolderOpen className="h-7 w-7 text-amber-600" /> Dokumente</h1>
          <p className="mt-1 text-sm text-slate-500">Alle Firmendokumente an einem Ort – Abnahme- und Informationsprotokolle, Behörden, Versicherung, Personal, Zertifikate. Keine Buchung.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={addFolder} data-testid="document-new-folder-btn"><FolderPlus className="h-4 w-4 mr-1.5" /> Neuer Ordner</Button>
          <Button onClick={() => { setForm(EMPTY); setUploadFile(null); setUploadOpen(true); }} className="bg-amber-600 hover:bg-amber-700" data-testid="document-upload-btn"><Plus className="h-4 w-4 mr-1.5" /> Dokument hochladen</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap border-b border-slate-200 pb-3">
        {[["company", company?.name || "Diese Firma"], ["all", "Alle Firmen"]].map(([id, label]) => (
          <button key={id} onClick={() => setScope(id)} data-testid={`document-scope-${id}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${scope === id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{label}</button>
        ))}
        <div className="relative ml-auto w-full sm:w-80">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche in Titel, Inhalt, Tags …" className="pl-8" data-testid="document-search-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        <Card className="p-2 h-fit" data-testid="document-folders">
          <button onClick={() => setFolder("")} data-testid="document-folder-all"
            className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 ${!folder ? "bg-amber-50 text-amber-900 font-medium" : "hover:bg-slate-50 text-slate-700"}`}>
            <FolderOpen className="h-4 w-4" /> Alle Ordner
          </button>
          {folders.map((f) => (
            <button key={f.name} onClick={() => setFolder(f.name)} data-testid={`document-folder-${f.name}`}
              className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between gap-2 ${folder === f.name ? "bg-amber-50 text-amber-900 font-medium" : "hover:bg-slate-50 text-slate-700"}`}>
              <span className="inline-flex items-center gap-2 truncate"><Folder className="h-4 w-4 shrink-0" />{f.name}</span>
              <span className="text-[11px] text-slate-400">{f.count}</span>
            </button>
          ))}
        </Card>

        <div>
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Dokumente …</div>
          ) : items.length === 0 ? (
            <Card className="p-10 text-center bg-slate-50 border-dashed border-2 border-slate-300" data-testid="documents-empty">
              <FolderOpen className="h-10 w-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-700 font-medium">{q ? "Keine Treffer" : "Noch keine Dokumente in diesem Ordner"}</p>
              <p className="text-sm text-slate-500 mt-1">Dokumente kommen per Posteingang («In Dokumente ablegen») oder über «Dokument hochladen» hierher.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="document-list">
              {items.map((d) => (
                <Card key={d.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`document-card-${d.id}`} data-highlight-id={d.id}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex gap-3 min-w-0">
                      {d.has_file && <DocThumb url={`/companies/${d.company_id}/documents/${d.id}/file`} title={d.file_name || d.title} onOpen={() => setPreview(d)} className="h-16 w-16" testid={`document-thumb-${d.id}`} />}
                      <h3 className="font-semibold text-slate-900 leading-tight">{d.title}</h3>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px] inline-flex items-center gap-1"><Folder className="h-3 w-3" />{d.folder}</Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(d.doc_date)}</span>
                    {scope === "all" && d.company_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{d.company_name}</span>}
                    {d.file_name && <span className="truncate max-w-[180px]">{d.file_name}</span>}
                  </div>
                  {d.summary && <p className="mt-2 text-xs text-slate-600 line-clamp-3">{d.summary}</p>}
                  {(d.tags || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">{d.tags.map((t) => <span key={t} className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-px text-[10px] text-slate-700"><Tag className="h-2.5 w-2.5" />{t}</span>)}</div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {d.ai_extracted && <Sparkles className="h-3 w-3 text-violet-500" title="KI-gelesen" />}
                      {d.has_file && <button type="button" onClick={() => setPreview(d)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`document-view-${d.id}`}><Eye className="h-3.5 w-3.5" /> Ansehen</button>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`document-edit-${d.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`document-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <DocPreviewDialog open={!!preview} onClose={() => setPreview(null)} title={preview?.file_name || preview?.title} mimeType={preview?.mime_type}
        url={preview ? `/companies/${preview.company_id}/documents/${preview.id}/file` : null} testid="document-preview" />

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="document-upload-dialog">
          <DialogHeader><DialogTitle>Dokument hochladen</DialogTitle></DialogHeader>
          <div className="border-2 border-dashed border-amber-300 bg-amber-50/50 rounded-lg p-4 text-center cursor-pointer hover:bg-amber-50" onClick={() => fileRef.current?.click()} data-testid="document-upload-zone">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.docx,.xlsx,.txt" className="hidden" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="document-upload-input" />
            <UploadCloud className="h-6 w-6 mx-auto text-amber-600" />
            <p className="text-sm text-slate-700 mt-1">{uploadFile ? uploadFile.name : "Datei wählen – die KI liest Titel, Datum und Schlagwörter"}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Titel (leer = KI-Vorschlag)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="document-input-title" /></div>
            <div><Label>Ordner</Label>
              <select value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="document-input-folder">
                <option value="">Automatisch (KI)</option>{folderOptions.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div><Label>Datum des Dokuments</Label><SwissDateField  value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} data-testid="document-input-date" /></div>
            <div className="col-span-2"><Label>Tags (Komma-getrennt)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="z.B. Umbau, Bahnhofstrasse" data-testid="document-input-tags" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Abbrechen</Button>
            <Button onClick={upload} disabled={uploading} className="bg-amber-600 hover:bg-amber-700" data-testid="document-upload-save-btn">{uploading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Ablegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDoc} onOpenChange={(o) => { if (!o) setEditDoc(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="document-edit-dialog">
          <DialogHeader><DialogTitle>Dokument bearbeiten</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Titel</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="document-edit-title" /></div>
            <div><Label>Ordner</Label>
              <select value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="document-edit-folder">
                {[...new Set([...folderOptions, form.folder].filter(Boolean))].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div><Label>Datum</Label><SwissDateField  value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} data-testid="document-edit-date" /></div>
            <div className="col-span-2"><Label>Tags</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="document-edit-tags" /></div>
            <div className="col-span-2"><Label>Zusammenfassung</Label><Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="document-edit-summary" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDoc(null)}>Abbrechen</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="document-edit-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
